import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { filtersTypes } from 'app-components/flux/filters';
import type RootState from 'app-components/utils/redux-store/rootState';
import { Map as ImmutableMap } from 'immutable';
import { LruObjectCache, createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';
import { getId } from '../core/resourceStoreTypes';

import store from '../../utils/redux-store';

import { CATEGORIES_REDUCER_KEY, type Category, CategoryTypeEnum, KNOWN_CATEGORY_ID } from './categoriesTypes';

const usageTypeSortOrder = ['BUSINESS', 'FARMING', 'RENTAL', 'PERSONAL'];

type State = Pick<RootState, typeof CATEGORIES_REDUCER_KEY>;

export const getCategoryStore = (state: State) => state[CATEGORIES_REDUCER_KEY];

// required default selectors
export const getResourcesById = (state: State) => getCategoryStore(state).resourcesById;
export const getLoadPending = (state: State) => getCategoryStore(state).loadPending;
export const getIsLoading = (state: State) => getCategoryStore(state).isLoading;
export const getError = (state: State) => getCategoryStore(state).error;
export const getLastSyncDate = (state: State) => getCategoryStore(state).lastSyncDate;

export const getCategoriesById = getResourcesById;
export const getCategoryById = (state: State, id: string) => getResourcesById(state).get(id);

export const getCategories = createSelector(getCategoriesById, (categoriesById) => categoriesById.toList());

export const getCategoriesByParentId = createSelector(getCategories, (categories) =>
  categories.groupBy((category) => category.parentId ?? '0'),
);

// When we have usage-types (used by CBP), useful to separate root categories into different buckets
// for furhter processing.
//
export const getL1CategoriesByUsageType = createSelector(getCategories, (categories) =>
  categories
    .filter((category) => !category.parentId || category.parentId === '0')
    .groupBy((category) => {
      let { usageType } = category;
      if (usageType !== 'PERSONAL' && usageType !== 'BUSINESS' && usageType !== 'RENTAL' && usageType !== 'FARMING') {
        usageType = 'PERSONAL';
      }
      return usageType;
    }),
);

// When we have usage-types (used by CBP), useful to separate root categories into different buckets
// for furthur processing.
//
export const getCategoriesIdsByUsageType = createSelector(getCategoriesById, (categoriesByIds) => {
  const groupedBy = categoriesByIds.valueSeq().groupBy((category) => {
    let { usageType } = category;
    if (usageType !== 'PERSONAL' && usageType !== 'BUSINESS' && usageType !== 'RENTAL' && usageType !== 'FARMING') {
      usageType = 'PERSONAL';
    }
    return usageType;
  });
  const sorted = groupedBy.sortBy((_, usageType) => {
    return usageTypeSortOrder.indexOf(usageType);
  });
  return sorted;
});

// Instead of a feature-flag for enabling usage-types, we use the presence of categories with different usage-type as
// an indicator to enable them. This is to handle the situation where customers downgraded but already has used
// categories of different usage-types. We don't want to just remove these categories and there will be enough other
// business features, unlikely customers would want to give those up while still using business categories.
//
export const enableCategoryUsageTypes = createSelector(
  getCategoriesIdsByUsageType,
  (categoriesByUsageType) => (categoriesByUsageType.size || 0) > 1,
);

export const getUsageTypes = createSelector(getCategoriesIdsByUsageType, (categoriesByUsageType) =>
  categoriesByUsageType.keySeq(),
);

// ----------------------------------------------------------
// getCategoryString
//
export function getCategoryString(
  categoriesById: ReturnType<typeof getCategoriesById>,
  id: string,
  noParent = false,
  depth = 0,
): string {
  if (depth && depth > 40) {
    return 'MAXDEPTH_EXCEEDED';
  }

  const catObj = categoriesById.get(id);

  if (catObj) {
    const catName = catObj.get('name') || '';
    const parentId = catObj.get('parentId');

    if (!noParent && parentId && parentId !== '0') {
      return `${getCategoryString(categoriesById, parentId, noParent, depth + 1)}:${catName}`;
    }

    return catName;
  }

  return '—';
}
// ----------------------------------------------------------
// getCategoryForId
//
export function getCategoryForId(state: State | null | undefined, id: string) {
  return getCategoriesById(state || store!.getState()).get(id);
}

export function isIncomeCat(state: State | null | undefined, id: string) {
  const cat = getCategoryForId(state, id);

  return cat && cat.type === CategoryTypeEnum.INCOME;
}

export function isExpenseCat(state: State | null | undefined, id: string) {
  const cat = getCategoryForId(state, id);

  return cat && cat.type === 'EXPENSE';
}

// returns categories sub-tree that holds categoryLeaves
export const filterCategoryTreeByLeaves = createSelector(
  (categoriesById: ReturnType<typeof getCategoriesById>, _categoryLeaves: unknown) => categoriesById,
  (_categoriesById: unknown, categoryLeaves: ReturnType<typeof getCategoriesById>) => categoryLeaves,
  (categoriesById, categoryLeaves) =>
    ImmutableMap().withMutations((categoryTree) => {
      forEachSubCategory(categoriesById, '0', true, (category, path) => {
        if (categoryLeaves.has(getId(category))) {
          categoryTree.set(category.id, category);
          path.forEach((parentCategory) => categoryTree.set(parentCategory.id, parentCategory));
        }
      });
    }),
);

// iterates recursively over categories
export const forEachSubCategory = (
  categories: ReturnType<typeof getCategoriesById>,
  parentCategoryId: string,
  preOrder: boolean,
  callback: (category: Category, path: Category[]) => void,
  path?: Category[],
) => {
  const childCategories = categories.filter((category) => category.parentId === parentCategoryId);
  childCategories.forEach((category) => {
    if (preOrder) {
      callback(category, path || []);
    }
    forEachSubCategory(categories, getId(category), preOrder, callback, [...(path || []), category]);
    if (!preOrder) {
      callback(category, path || []);
    }
  });
};

export const getChildCategoriesByParentIdUtil = (
  categories: ReturnType<typeof getCategoriesById>,
  parentCategoryId: string,
) => {
  let childCategories = ImmutableMap<string, Category>();

  forEachSubCategory(categories, parentCategoryId, false, (category) => {
    childCategories = childCategories.set(getId(category), category);
  });

  return childCategories;
};

export const getChildCategoriesByParentId = createCachedSelector(
  [getCategoriesById, (_: State, parentCategoryId: string) => parentCategoryId],
  getChildCategoriesByParentIdUtil,
)({ keySelector: (_, id) => id ?? '', cacheObject: new LruObjectCache({ cacheSize: 6 }) });

// Filter is any due to strange bugs with filterTypes.Filter not recognizing chartOfAccounts as a property of an obj of filterItems.
export const getParentFromCoaListFunction = (
  categories: ReturnType<typeof getCategoriesById>,
  filter: any,
): string | undefined => {
  const coaList = filter?.filterItems?.[0]?.chartOfAccounts;
  if (!coaList || coaList.length === 0) return '';

  // For quick lookup (Thanks ChatGPT)
  const coaIdSet = new Set(coaList.map((coa: chartOfAccountsTypes.ChartOfAccountProps) => coa.id));

  // We want to keep track of what coas we have visited in case there is a loop.
  const visited = new Set<string | undefined>();

  const startingNode = coaList[0]?.id;
  let currentCategory = categories.get(startingNode);
  let lastFoundInList: string | undefined = startingNode; // Track the highest-level category found in COA list

  while (
    currentCategory &&
    currentCategory.parentId &&
    coaIdSet.has(currentCategory.id) &&
    // If the next parent is a node we have already been to then we should exit
    !visited.has(currentCategory.parentId)
  ) {
    visited.add(currentCategory.id);
    if (coaIdSet.has(currentCategory.parentId)) {
      lastFoundInList = currentCategory.parentId; // Update if parent is in list
    }
    currentCategory = categories.get(currentCategory.parentId); // Move up to the parent
  }

  return lastFoundInList || '';
};

// We grab the parent category to display for PSIs, if none are parents, we just grab the first item in the list
export const getParentFromCoaList = createSelector(
  getCategoriesById,
  (state, filter: filtersTypes.Filter) => filter,
  (categories, filter) => getParentFromCoaListFunction(categories, filter),
);

export const getChildCategoryIdsByParentId = createCachedSelector(
  (_: State, categoryId: string) => categoryId,
  getChildCategoriesByParentId,
  (_categoryId, childCategories) => {
    return childCategories.valueSeq().toArray().map(getId);
  },
)({ keySelector: (_, id) => id ?? '', cacheObject: new LruObjectCache({ cacheSize: 6 }) });

export const getIncomeCOAIds = createSelector(getCategoriesById, (categories) =>
  categories
    .filter((category) => category.type === CategoryTypeEnum.INCOME)
    .map(getId)
    .toSet(),
);

export const getExpenseCOAIds = createSelector(getCategoriesById, (categories) =>
  categories
    .filter((category) => category.type === CategoryTypeEnum.EXPENSE)
    .map(getId)
    .toSet(),
);

export const getPersonalIncomeCategory = createSelector(
  getCategoriesById,
  (categories) =>
    categories.find(
      (category) =>
        category.knownCategoryId === KNOWN_CATEGORY_ID['PERSONAL_INCOME'] && category.usageType !== 'BUSINESS',
    ) ||
    categories.find((category) => category.name === 'Personal Income' && category.type === CategoryTypeEnum.INCOME),
);

export const getBusinessIncomeCategory = createSelector(
  getCategoriesById,
  (categories) =>
    categories.find(
      (category) =>
        category.knownCategoryId === KNOWN_CATEGORY_ID['BUSINESS_INCOME'] && category.usageType === 'BUSINESS',
    ) ||
    categories.find((category) => category.name === 'Business Income' && category.type === CategoryTypeEnum.INCOME),
);

export const getFederalTaxCategory = createSelector(
  getCategoriesById,
  (categories) =>
    categories.find((category) => category.knownCategoryId === KNOWN_CATEGORY_ID.FEDERAL_TAX) ||
    categories.find((category) => category.name === 'Federal Tax' && category.type === CategoryTypeEnum.EXPENSE),
);

export const getDefaultWatchlistCategory = (state: State) => {
  const categories = getCategoriesById(state);
  // this gets the 'Dining and Drinks' category based on known id
  const eatingOut = categories.find((category) => category.knownCategoryId === '2250000000');
  return eatingOut
    ? categories.filter((category) => category.parentId === eatingOut.id || category.id === eatingOut.id)
    : undefined;
};

export const getCategoriesForIds = createCachedSelector(
  getCategoriesById,
  (_state: unknown, categoryIds: string[]) => categoryIds,
  (categories, ids) => categories.filter((category) => ids.includes(getId(category))),
)({
  keySelector: (_state, categoryIds) => categoryIds?.join(':') || 'none',
  cacheObject: new LruObjectCache({ cacheSize: 6 }),
});

export const getInvestmentCategories = createSelector(getResourcesById, (categories) =>
  categories.filter((category) => category.isInvestment),
);

export const getTxfIdMap = createSelector(getResourcesById, (categories) => {
  const txfIdMap: { [key: string]: string[] } = {};
  categories.forEach((cat) => {
    const id = getId(cat);
    const { txfIds, txfId } = cat;
    if (txfIds) {
      Object.values(txfIds)?.forEach((value) => {
        if (value) {
          txfIdMap[value] = txfIdMap[value] || [];
          txfIdMap[value].push(id);
        }
      });
    }
    if (txfId && !(txfId in txfIdMap)) {
      txfIdMap[txfId] = txfIdMap[txfId] || [];
      txfIdMap[txfId].push(id);
    }
  });
  return txfIdMap;
});

export const getBusinessCoaIds = createSelector([getCategoriesIdsByUsageType], (categories) =>
  categories
    .get('BUSINESS')
    ?.map((category) => category.id)
    .toList()
    .toArray(),
);

export const getPersonalCoaIds = createSelector([getCategoriesIdsByUsageType], (categories) =>
  categories
    .get('PERSONAL')
    ?.map((category) => category.id)
    .toList()
    .toArray(),
);

export const getBusinessIncomeCoaIds = createSelector([getCategoriesIdsByUsageType], (categories) => {
  return (
    categories
      .get('BUSINESS')
      ?.filter((category) => category.type === 'INCOME')
      ?.map((category) => category.id) ?? []
  );
});
